import type { CustomVariablesFormState } from '@openx/types/targeting/advancedTargeting';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import { type JSX, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { AdvancedTargetingSectionReadOnly } from './AdvancedTargetingSectionReadOnly';
import { CustomVariablesDrawerItems } from './CustomVariables/CustomVariablesDrawerItems';
import { CustomVariablesItems } from './CustomVariables/CustomVariablesItems';
import { CustomVariablesTopBox } from './CustomVariables/CustomVariablesTopBox/CustomVariablesTopBox';
import { useCustomVariablesDrawerState } from './hooks/useDrawerState';

/** Custom Targeting card with the standalone contextual segments flag on: edits the custom variables field. */
export const AdvancedTargeting = memo(function AdvancedTargeting({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
}: TargetingProps<CustomVariablesFormState>): JSX.Element {
  const { t } = useTranslation();
  const { initState, params, setParams, restore, onEnter, isUpdated, fieldValue } =
    useCustomVariablesDrawerState(targetingParams);

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdate(field, fieldValue),
    restoreParams: restore,
  });

  // The field can change while the drawer is closed (e.g. removed from the card), so start from its current value
  const onOpenDrawerClick = useCallback(() => {
    restore();
    onDrawerOpen();
  }, [restore, onDrawerOpen]);

  const onRemove = useCallback(() => onFieldUpdate(field, null), [field, onFieldUpdate]);

  return (
    <>
      <AdvancedTargetingSectionReadOnly
        name={name}
        field={field}
        sectionName={t('Custom Variables')}
        description={t('Use custom data signals to create highly granular, rule-based targeting strategies.')}
        hasValue={!!initState.custom.length}
        readonly={readonly}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemove}
      >
        <CustomVariablesItems readonly targetingParams={initState} />
      </AdvancedTargetingSectionReadOnly>

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isUpdated}>
          <CustomVariablesTopBox items={params} onEnter={onEnter} />

          <CustomVariablesDrawerItems params={params} updateParams={setParams} />
        </TargetingDrawer>
      )}
    </>
  );
});
